import { useEffect, useState } from "react";
import { CreditCard, Smartphone, Truck, Sparkles, Zap } from "lucide-react";
import { api } from "@/lib/api";

const euro = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `€ ${Number(n).toFixed(2).replace(".", ",")}`;
};

export default function CardPricingCard({ onPayNow }) {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/card-pricing/public");
        setPricing(data);
      } catch {
        setPricing(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!pricing) return null;

  const minFree = Number(pricing.free_shipping_from_qty) || 3;
  const shipping = Number(pricing.shipping_cost) || 0;
  const bundles = ["1", "2", "3", "4", "5"];

  return (
    <div
      data-testid="pricing-card-section"
      className="mt-6 rounded-xl border border-[var(--border-c)] bg-gradient-to-br from-[var(--bg-2)] to-[var(--bg-3)]/60 p-6 md:p-7"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[#00F2F4] font-semibold">
            Listino & Pagamenti
          </div>
          <h3 className="mt-1 font-serif text-xl text-[var(--fg-1)] tracking-tight">
            Ordina in un click
          </h3>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-[#3E7A5E]/12 text-[#3E7A5E]">
          <Truck size={11} strokeWidth={2.2} /> Spedizione gratis da {minFree} card
        </span>
      </div>

      {/* Pricing grid — mobile: scroll orizzontale con 2 card visibili (snap); md+: griglia a 5 */}
      <div
        data-testid="pricing-tiers-scroll"
        className="-mx-6 px-6 md:mx-0 md:px-0 overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-thin"
      >
        <div className="flex md:grid md:grid-cols-5 gap-2 pb-3 md:pb-0">
          {bundles.map((k) => {
            const qty = Number(k);
            const price = Number(pricing.bundle_prices?.[k]);
            const freeShip = qty >= minFree;
            return (
              <div
                key={k}
                data-testid={`pricing-tier-${k}`}
                className="snap-start flex-shrink-0 w-[calc(50%-0.25rem)] md:w-auto rounded-lg border border-[var(--border-c)] bg-[var(--bg-1)] p-3 text-center"
              >
                <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--fg-3)] font-sans">
                  {k} {qty === 1 ? "card" : "card"}
                </div>
                <div className="mt-1.5 font-serif text-lg text-[var(--fg-1)]">{euro(price)}</div>
                <div className="mt-1 text-[9px] uppercase tracking-[0.1em]">
                  {freeShip ? (
                    <span className="text-[#3E7A5E] font-sans font-semibold">+ ship. gratis</span>
                  ) : (
                    <span className="text-[var(--fg-4)] font-sans">+ {euro(shipping)} sped.</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="md:hidden mt-1 text-[10px] text-[var(--fg-3)] font-sans text-center" data-testid="pricing-scroll-hint">
        ← Scorri per vedere altre opzioni →
      </div>

      {/* Payment methods */}
      <div className="mt-6 pt-5 border-t border-[var(--border-c)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans mb-2">
              Metodi di pagamento accettati
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-1)] border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans">
                <CreditCard size={12} strokeWidth={1.8} /> Carta
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-1)] border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans">
                <Smartphone size={12} strokeWidth={1.8} /> Apple Pay
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bg-1)] border border-[var(--border-c)] px-3 py-1.5 text-xs text-[var(--fg-1)] font-sans">
                <Smartphone size={12} strokeWidth={1.8} /> Google Pay
              </span>
            </div>
          </div>
          {onPayNow && (
            <button
              type="button"
              data-testid="pricing-card-cta"
              onClick={onPayNow}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-2.5 text-sm font-sans font-semibold"
              style={{ transition: "background-color 0.2s ease" }}
            >
              <Zap size={14} strokeWidth={2.2} />
              Ordina e paga ora
            </button>
          )}
        </div>
        <p className="mt-3 text-[11px] text-[var(--fg-3)] leading-relaxed">
          <Sparkles size={10} className="inline mr-1 -mt-0.5" />
          Al momento dell'ordine potrai scegliere se pagare subito con Google Pay, Apple Pay o carta, oppure salvare la richiesta e pagare più tardi dal link inviato via email.
        </p>
      </div>
    </div>
  );
}
